package com.essntl.features.category.ui.addeditcategory

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.category.domain.model.CategoryModel
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.category.domain.usecase.AddEditCategoryUseCase
import com.essntl.features.file.domain.repository.FileRepository
import com.essntl.features.service.domain.repository.ServiceRepository
import com.essntl.features.supplier.domain.repository.SupplierRepository
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update

class AddEditCategoryViewModel(
    private val categoryRepository: CategoryRepository,
    private val addEditCategoryUseCase: AddEditCategoryUseCase,
    private val serviceRepository: ServiceRepository,
    private val supplierRepository: SupplierRepository,
    private val fileRepository: FileRepository,
    private val toastManager: ToastManager,
    private val state: MutableStateFlow<AddEditCategoryState>,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    fun onEvent(event: AddEditCategoryEvent) {
        when (event) {
            is AddEditCategoryEvent.GetCategoryIcons -> getCategoryIcons()
            is AddEditCategoryEvent.GetCategory -> getCategory(event)
            is AddEditCategoryEvent.UpdateCategoryFields -> updateCategoryFields(event)
            is AddEditCategoryEvent.AddCategory -> addCategory()
            is AddEditCategoryEvent.EditCategory -> editCategory()
            is AddEditCategoryEvent.DeleteCategory -> deleteCategory()
            is AddEditCategoryEvent.ClearState -> clearState()
        }
    }

    private fun getCategoryIcons() =
        getData(
            getData = { fileRepository.getBucketUrlFiles("icons") },
            getState = { state.value.getCategoryIconsUrl },
            setState = { state.value = state.value.copy(getCategoryIconsUrl = it) },
        )

    private var getCategoryJob: Job? = null
    private fun getCategory(event: AddEditCategoryEvent.GetCategory) {
        getCategoryJob?.cancel()
        getCategoryJob = getData(
            getData = { categoryRepository.getById(event.id) },
            getState = { state.value.getCategory },
            setState = {
                state.value = state.value.copy(
                    getCategory = it,
                    isEditEnabled = false,
                )
            },
            onSuccess = { c ->
                state.value.category.value = c
                getCountServices()
                getCountSuppliers()
            },
        )
    }

    private fun getCountServices() =
        getData(
            getData = { serviceRepository.getCountServicesByCategory(state.value.category.value.id) },
            getState = { state.value.getServicesCategoryCount },
            setState = { state.value = state.value.copy(getServicesCategoryCount = it) },
        )

    private fun getCountSuppliers() =
        getData(
            getData = { supplierRepository.getCountSuppliersByCategory(state.value.category.value.id) },
            getState = { state.value.getSuppliersCategoryCount },
            setState = { state.value = state.value.copy(getSuppliersCategoryCount = it) },
        )

    private fun updateCategoryFields(event: AddEditCategoryEvent.UpdateCategoryFields) {
        state.value.category.value = event.category
        val newCategory =
            state.value.getCategory.data?.getChangedFields(state.value.category.value) ?: return
        state.update {
            it.copy(isEditEnabled = !newCategory.isEmpty)
        }
    }

    private fun addCategory() =
        getData(
            getData = { addEditCategoryUseCase.insertCategory(state.value.category.value) },
            getState = { state.value.addEditCategory },
            setState = { state.value = state.value.copy(addEditCategory = it) },
            onSuccess = { p ->
                state.update {
                    it.copy(
                        getCategory = State.success(p),
                    )
                }

                state.value.category.value = p

                toastManager.success(
                    description = "Category created successfully",
                )
            },
        )

    private fun editCategory() =
        getData(
            getData = { addEditCategoryUseCase.updateCategory(state.value.category.value) },
            getState = { state.value.addEditCategory },
            setState = { state.value = state.value.copy(addEditCategory = it) },
            onSuccess = { ta ->
                state.update {
                    it.copy(
                        getCategory = State.success(ta),
                    )
                }

                state.value.category.value = ta

                toastManager.success(
                    description = "Category updated successfully",
                )
            },
        )

    private fun deleteCategory() =
        getData(
            getData = { categoryRepository.delete(state.value.category.value) },
            getState = { state.value.deleteCategory },
            setState = { state.value = state.value.copy(deleteCategory = it) },
            onSuccess = {
                toastManager.success(
                    description = "Category deleted successfully",
                )
            },
        )

    private fun clearState() {
        state.value = state.value.copy(
            getCategory = State(),
            addEditCategory = State(),
            deleteCategory = State(),
            getServicesCategoryCount = State(),
            getSuppliersCategoryCount = State(),
            getCategoryIconsUrl = State(),
            isEditEnabled = false,
        )
        state.value.category.value = CategoryModel.empty
    }
}